import { removeAndCleanLink } from "@/utils/helper"
import { findItemByName } from "./navigationHelper"

const LANGUAGE_PROPERTY_MAPPINGS = [
  {
    nameKey: "languageOne",
    abbreviationKey: "languageOneAbbreviation",
    linkKey: "languageOneLink",
  },
  {
    nameKey: "languageTwo",
    abbreviationKey: "languageTwoAbbreviation",
    linkKey: "languageTwoLink",
  },
  {
    nameKey: "languageThree",
    abbreviationKey: "languageThreeAbbreviation",
    linkKey: "languageThreeLink",
  },
]

// Case-insensitive match of the site's active language against a country's
// language name or abbreviation, so it works whether AEM/env supplies the
// language as a name ("English"), an abbreviation ("EN") or a code ("en").
const matchesCurrentLanguage = (name, abbreviation, currentLanguage) => {
  if (!currentLanguage) return false
  const target = String(currentLanguage).trim().toLowerCase()
  return [name, abbreviation].some(
    value => value && String(value).trim().toLowerCase() === target
  )
}

const createLanguageObject = (
  language,
  abbreviation,
  link,
  width,
  isCurrent = false
) => {
  if (!language) return null
  const sanitizedLink = removeAndCleanLink(link)
  return {
    text: width < 1024 ? language : abbreviation,
    href: sanitizedLink,
    // Flag the language of the site the user is currently on so screen readers
    // (e.g. JAWS) announce its current state (a11y: KBNADX language selector).
    ...(isCurrent ? { "aria-current": "true" } : {}),
    trackingAttributes: {
      "data-datalayer-id": "kds-element-navigation",
      "data-datalayer-data": JSON.stringify({
        clickInternalLinks: "true",
        eventAction: "language selector",
        eventName: "language selector",
        eventType: "language selector",
        internalLinkName: `language select:${abbreviation.toLowerCase()}`,
        internalLinkPosition: "languageselector",
        internalLinkType: "homepage:navigation",
        internalLinkURL: sanitizedLink,
        internalLinkZoneName: "home:global header",
      }),
    },
  }
}

const mapRegion = (
  regionName = "",
  items = [],
  currentCountry = "",
  width = 0,
  currentLanguage = ""
) => {
  const normalize = value =>
    String(value || "")
      .trim()
      .toLowerCase()

  return {
    name: regionName,
    countries: items
      .map(item => {
        if (!item.countryName && !item.country) return null
        const itemCountryName = item.countryName || item.country || ""
        const country = { name: itemCountryName }
        // Match the site's current country against either the display name or
        // the country code, since AEM/env may supply the country in either form
        // (e.g. "United States" vs "us"). Without this the current country is
        // never detected, so neither the grey-out nor aria-current is applied.
        const normalizedCurrentCountry = normalize(currentCountry)
        const isCurrentCountry =
          !!normalizedCurrentCountry &&
          [item.countryName, item.country].some(
            value => value && normalize(value) === normalizedCurrentCountry
          )
        const languages = LANGUAGE_PROPERTY_MAPPINGS.map(
          ({ nameKey, abbreviationKey, linkKey }) =>
            createLanguageObject(
              item[nameKey],
              item[abbreviationKey],
              item[linkKey],
              width,
              // Only the active language within the country the user is
              // currently on is marked current, so aria-current isn't set on
              // the same language listed under other countries.
              isCurrentCountry &&
                matchesCurrentLanguage(
                  item[nameKey],
                  item[abbreviationKey],
                  currentLanguage
                )
            )
        ).filter(Boolean)

        if (item.countryLink) {
          country.href = removeAndCleanLink(item.countryLink)
          country.trackingAttributes = {
            "data-datalayer-id": "kds-element-navigation",
            "data-datalayer-data": JSON.stringify({
              clickInternalLinks: "true",
              eventAction: "country selector",
              eventName: "country selector",
              eventType: "country selector",
              internalLinkName: `country select:${item.country.toLowerCase()}`,
              internalLinkPosition: "global header",
              internalLinkType: "homepage:navigation",
              internalLinkURL: "n/a",
              internalLinkZoneName: "home:global header",
            }),
          }
        }
        if (isCurrentCountry) country.isCurrentCountry = true
        if (languages.length) country.languages = languages

        return country
      })
      .filter(Boolean),

    trackingAttributes: {
      "data-datalayer-id": "kds-element-navigation",
      "data-datalayer-data": JSON.stringify({
        clickInternalLinks: "true",
        eventAction: `country select:${regionName.toLowerCase()}`,
        eventName: `country select:${regionName.toLowerCase()}`,
        eventType: "navigation",
        internalLinkName: regionName.toLowerCase(),
        internalLinkPosition: "countryselector",
        internalLinkType: "global header : navigation",
        internalLinkURL: "n/a",
        internalLinkZoneName: "global header",
      }),
    },
  }
}

export const getLanguageMenu = (container, width) => {
  let secondLanguageCTA = {}
  const {
    country = "",
    language = "",
    enableLanguageSwitcher,
    languageLabel = "",
    languageUrl = "",
  } = container || {}

  const currentCountry = process.env.NEXT_PUBLIC_COUNTRYNAME || country
  const currentLanguage = language || process.env.NEXT_PUBLIC_LANGUAGENAME
  const countrySelector = findItemByName(container, "countryselector")
  if (!countrySelector) {
    return {
      regions: [],
      languageMenuTitle: undefined,
      languageMenuDescription: undefined,
      currentCountry,
      currentLanguage,
      secondLanguageCTA,
    }
  }

  const regionMappings = [
    { key: "americasItems", name: "Americas" },
    { key: "africaItems", name: "Africa" },
    { key: "asiaItems", name: "Asia" },
    { key: "oceaniaItems", name: "Australasia" },
    { key: "middleEastItems", name: "Middle East" },
    { key: "europeItems", name: "Europe" },
  ]

  const regions = regionMappings
    .filter(region => countrySelector[region.key])
    .map(region =>
      mapRegion(
        region.name,
        countrySelector[region.key],
        currentCountry,
        width,
        currentLanguage
      )
    )

  if (enableLanguageSwitcher) {
    secondLanguageCTA = {
      text: languageLabel,
      href: removeAndCleanLink(languageUrl),
    }
  }

  return {
    regions,
    languageMenuTitle: countrySelector.title,
    languageMenuDescription: countrySelector.description,
    currentCountry,
    currentLanguage,
    secondLanguageCTA,
  }
}
